import { Manga, MangaSource, KnownSite } from '../types/manga';
import { getSourceName, getTodayKey, getLatestReleaseAt } from '../utils/helpers';

export interface ChapterCheckResult {
  sourceUrl: string;
  sourceName: string;
  latestChapter: number;
  /** epoch ms the site published latestChapter, when the page lists it */
  latestChapterAt?: number;
  error?: string;
  isMismatched?: boolean;
  pageTitle?: string;
  /** latestChapter is the top chapter on the site's own page (or its live feed), not a carried-over value */
  fromPage?: boolean;
}

/** Sources whose page was opened this session just to read a missing release date */
const datedThisSession = new Set<string>();
// 1668 pages go through Jina (~18 calls/min shared by every check), so date-only lookups are rationed:
// a full check must not stall behind them. Cards still missing a date get theirs on later rounds.
const DATE_LOOKUP_WINDOW_MS = 10 * 60 * 1000;
const DATE_LOOKUPS_PER_WINDOW = 6;
const dateLookupTimes: number[] = [];

function takeDateLookupSlot(): boolean {
  const now = Date.now();
  while (dateLookupTimes.length > 0 && now - dateLookupTimes[0] >= DATE_LOOKUP_WINDOW_MS) dateLookupTimes.shift();
  if (dateLookupTimes.length >= DATE_LOOKUPS_PER_WINDOW) return false;
  dateLookupTimes.push(now);
  return true;
}

const STOP_WORDS = new Set([
  'the', 'of', 'in', 'and', 'a', 'an', 'to', 'for', 'with', 'on', 'at', 'from', 'by',
  'is', 'are', 'was', 'were', 'แห่ง', 'ของ', 'ใน', 'ที่', 'กับ', 'และ', 'การ', 'เรื่อง',
  'แปลไทย', 'อ่านมังงะ', 'มังงะ', 'ฟรี', 'ตอนล่าสุด', 'ตอนที่', 'ตอน', 'มังฮวา', 'มังงะจีน',
  'manga', 'manhua', 'manhwa', 'webtoon', 'comic', 'comics', '1668manga', 'upmanga', 'mangablackcat'
]);

export function getSignificantWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9ก-๙]+/gi, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 2 && !STOP_WORDS.has(w));
}

export function calculateTitleMatchScore(
  userTitle: string,
  candidateText: string,
  candSlug?: string
): { score: number; matchedCount: number; totalUserWords: number; precision: number; recall: number; f1: number } {
  let decodedCand = candidateText;
  try {
    decodedCand = decodeURIComponent(candidateText);
  } catch {}

  const normUser = userTitle.toLowerCase().replace(/[^a-z0-9ก-๙]+/gi, ' ').trim();
  const normCand = decodedCand.toLowerCase().replace(/[^a-z0-9ก-๙]+/gi, ' ').trim();
  const userSlug = normUser.replace(/\s+/g, '-');

  // Exact slug match = instant top score
  if (candSlug && (candSlug === userSlug || candSlug === `${userSlug}-th` || candSlug === `manga-${userSlug}`)) {
    return { score: 1.0, matchedCount: 1, totalUserWords: 1, precision: 1.0, recall: 1.0, f1: 1.0 };
  }

  const userWords = getSignificantWords(userTitle);
  const candWords = getSignificantWords(decodedCand);

  if (userWords.length === 0) {
    return { score: 0, matchedCount: 0, totalUserWords: 0, precision: 0, recall: 0, f1: 0 };
  }

  let matchedCount = 0;
  for (const w of userWords) {
    if (candWords.some((cw) => cw === w)) {
      matchedCount += 1;
    } else if (w.length >= 4 && candWords.some((cw) => cw.startsWith(w) || w.startsWith(cw))) {
      matchedCount += 0.85;
    }
  }

  let candMatched = 0;
  for (const cw of candWords) {
    if (userWords.some((w) => cw === w || (w.length >= 4 && (cw.startsWith(w) || w.startsWith(cw))))) {
      candMatched++;
    }
  }

  const recall = Math.min(1.0, matchedCount / userWords.length);
  // Calculate precision primarily on Latin/English candidate words if available to allow Thai subtitles on English titles
  const isEnglishCandWords = candWords.filter(
    (w) => /^[a-z0-9]+$/i.test(w) && !['action', 'manhwa', 'manhua', 'up', 'raw', 'ตอน', 'th'].includes(w)
  );
  let matchedEnglish = 0;
  for (const cw of isEnglishCandWords) {
    if (userWords.some((w) => w === cw || (w.length >= 4 && (cw.startsWith(w) || w.startsWith(cw))))) {
      matchedEnglish++;
    }
  }

  const precision = isEnglishCandWords.length > 0
    ? matchedEnglish / isEnglishCandWords.length
    : (candWords.length > 0 ? candMatched / candWords.length : recall);

  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;

  // Exact phrase containment bonus (e.g. "pick me up" inside "pick me up infinite gacha")
  let bonus = 0;
  if (normUser.length >= 4 && normCand.includes(normUser)) {
    bonus = 0.25;
  }

  let score = (recall * 0.7) + (precision * 0.3) + bonus;

  // For short titles (<= 3 words), if ANY word is missing, penalize score heavily!
  if (userWords.length <= 3 && matchedCount < userWords.length && bonus === 0) {
    score = score * 0.4;
  }

  return { score: Math.min(1.0, score), matchedCount, totalUserWords: userWords.length, precision, recall, f1 };
}

export function extractPageTitle(html: string): string {
  if (!html) return '';

  // 1. Jina Reader Markdown Title: "Title: [title]"
  const jinaTitleMatch = html.match(/^Title:\s*([^\r\n]+)/m);
  if (jinaTitleMatch && jinaTitleMatch[1]) {
    return jinaTitleMatch[1].trim();
  }

  const ogMatch = html.match(/property=["']og:title["'][^>]*content=["']([^"']+)["']/i);
  let raw = ogMatch ? ogMatch[1] : '';

  if (!raw) {
    const entryMatch = html.match(
      /class=["'][^"']*(?:entry-title|post-title)[^"']*["'][^>]*>([\s\S]*?)<\/[a-z0-9]+>/i
    );
    if (entryMatch) {
      raw = entryMatch[1].replace(/<[^>]+>/g, '');
    }
  }

  if (!raw) {
    const titleTagMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    if (titleTagMatch) {
      raw = titleTagMatch[1];
    }
  }

  if (!raw) {
    const h1Match = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    if (h1Match) {
      raw = h1Match[1].replace(/<[^>]+>/g, '');
    }
  }

  if (!raw) {
    const mdH1Match = html.match(/^#\s+([^\r\n]+)/m);
    if (mdH1Match) {
      raw = mdH1Match[1];
    }
  }

  let cleaned = raw
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();

  // Strip common SEO boilerplate suffixes e.g. " - 1668Manga - เว็บอ่านมังงะ...", " | MangaBlackCat", " - Up-Manga"
  cleaned = cleaned
    .replace(/\s*[-–|]\s*(?:1668\s*manga|up[- ]?manga|mangablackcat|เว็บอ่านมังงะ|อ่านมังงะ|มังงะแปลไทย|ตอนที่|manhua|manhwa)[\s\S]*$/i, '')
    .trim();

  return cleaned || raw.trim();
}

export function isPageTitleMatching(
  expectedTitle: string,
  pageHtml: string
): { matches: boolean; pageTitle: string; score: number } {
  if (!pageHtml || !expectedTitle.trim()) {
    return { matches: false, pageTitle: '', score: 0 };
  }

  const pageTitle = extractPageTitle(pageHtml);
  if (!pageTitle) {
    return { matches: false, pageTitle: '', score: 0 };
  }

  // 1. Direct normalized phrase check:
  // If page title literally contains the entire expected title (e.g. "Call of the Spear" inside "Call of the Spear - 1668Manga...")
  const normExpected = expectedTitle.toLowerCase().replace(/[^a-z0-9ก-๙]+/gi, ' ').trim();
  const normPageTitle = pageTitle.toLowerCase().replace(/[^a-z0-9ก-๙]+/gi, ' ').trim();
  if (normExpected.length > 2 && normPageTitle.includes(normExpected)) {
    return { matches: true, pageTitle, score: 1.0 };
  }

  const { score, matchedCount, totalUserWords, precision } = calculateTitleMatchScore(expectedTitle, pageTitle);
  // Short titles (<= 3 words) MUST have 100% keyword match (all words matched)
  // Longer titles require at least 65% recall and matchedCount >= 3
  const matches = totalUserWords <= 3
    ? (matchedCount === totalUserWords && (precision >= 0.3 || normPageTitle.startsWith(normExpected.split(' ')[0])))
    : (score >= 0.65 && matchedCount >= 3);

  return { matches, pageTitle, score };
}

export function normalizeToMangaUrl(candidateUrl: string): string | null {
  let decoded = candidateUrl;
  try {
    decoded = decodeURIComponent(candidateUrl);
  } catch {}

  let urlObj: URL;
  try {
    urlObj = new URL(decoded);
  } catch {
    return null;
  }

  const hostname = urlObj.hostname.toLowerCase();
  let pathname = '';
  try {
    pathname = decodeURIComponent(urlObj.pathname).replace(/\/+$/, '');
  } catch {
    pathname = urlObj.pathname.replace(/\/+$/, '');
  }

  const chapterPatterns = [
    /^\/manga\/([^\/]+)\/(?:ตอนที่?|chapter|chap|ch)[-_/]?\d+/i,
    /^\/([^\/]+?)[-_]+(?:ตอนที่?|chapter|chap|ch)[-_/]?\d+/i,
  ];

  for (const pat of chapterPatterns) {
    const match = pathname.match(pat);
    if (match && match[1]) {
      const slug = match[1].replace(/^-+|-+$/g, '');
      if (hostname.includes('1668manga.com')) {
        return `https://1668manga.com/manga/${slug}/`;
      } else if (hostname.includes('up-manga.com')) {
        return `https://www.up-manga.com/${slug}/`;
      } else if (hostname.includes('mangablackcat.com')) {
        return `https://mangablackcat.com/manga/${slug}?sort=desc`;
      } else {
        return `${urlObj.origin}/manga/${slug}/`;
      }
    }
  }

  if (hostname.includes('1668manga.com') && !pathname.startsWith('/manga/')) {
    const parts = pathname.replace(/^\/+/, '').split('/');
    if (parts.length === 1 && parts[0]) {
      return `https://1668manga.com/manga/${parts[0].replace(/^-+|-+$/g, '')}/`;
    }
  }

  if (hostname.includes('mangablackcat.com')) {
    const slugMatch = pathname.match(/\/manga\/([^\/?#]+)/i);
    const slug = slugMatch ? slugMatch[1] : pathname.replace(/^\/+/, '');
    return `https://mangablackcat.com/manga/${slug}?sort=desc`;
  }

  return `${urlObj.origin}${pathname}/`;
}

export function extractSlug(sourceUrl: string): string | null {
  const match = sourceUrl.match(/\/manga\/([^\/?#]+)/i);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  // Fallback: /slug/ or /slug/?query
  const match2 = sourceUrl.match(/^https?:\/\/[^\/]+\/([^\/?#]+)\/?(?:[?#].*)?$/i);
  if (match2) {
    try {
      return decodeURIComponent(match2[1]);
    } catch {
      return match2[1];
    }
  }
  return null;
}

/**
 * Robust chapter extractor from HTML or Markdown
 */
export function findLatestChapterFromPage(content: string, sourceUrl: string): number {
  if (!content) return 0;

  const slug = extractSlug(sourceUrl);
  // If no slug or is just home domain, skip to avoid parsing random site chapters
  if (!slug || sourceUrl.replace(/^https?:\/\/[^\/]+\/?$/i, '') === '') {
    return 0;
  }

  // 1. Safe decode percent-encoded UTF-8 sequences without throwing on stray % signs in CSS/text
  let normalized = content.replace(/(?:%[0-9a-fA-F]{2})+/g, (seq) => {
    try {
      return decodeURIComponent(seq);
    } catch {
      return seq;
    }
  });
  normalized = normalized.replace(/\\\//g, '/').replace(/&amp;/g, '&');

  let maxCh = 0;
  const isSafeChapterNum = (val: number): boolean => {
    return !isNaN(val) && val > 0 && val < 5000 && !(val >= 2020 && val <= 2030);
  };

  // 2. Locate chapter list container to strictly isolate from sidebars, headers, and footers
  const chapterListMatch = normalized.match(
    /<(?:div|ul)\b[^>]*(?:id=["']chapterlist["']|class=["'][^"']*(?:eplister|listing-chapters_list|clstyle|wp-manga-chapter)[^"']*["'])[^>]*>([\s\S]*?)<\/(?:div|ul)>/i
  );
  const searchArea = chapterListMatch ? chapterListMatch[1] : normalized;

  // Pattern 1: data-num="155" (Native to WP-Manga, Madara, up-manga, 1668manga chapter lists)
  // When present it is authoritative: up-manga drops the decimal point from chapter URLs
  // (chapter 125.5 -> /slug-ตอนที่-1255/), so URL-based patterns below would read 1255.
  const dataNumRegex = /data-num=["'](\d+(?:\.\d+)?)["']/gi;
  let match: RegExpExecArray | null;
  while ((match = dataNumRegex.exec(normalized)) !== null) {
    const val = parseFloat(match[1]);
    if (isSafeChapterNum(val) && val > maxCh) {
      maxCh = val;
    }
  }
  if (maxCh > 0) return maxCh;

  // Pattern 2: Links containing the manga's specific slug e.g. /slug-ตอนที่-156/, /slug-ep-156/, or /manga/slug/156
  // The number must end at a URL boundary, otherwise cover images like "Limitless-Predation-716x1024.webp"
  // are read as chapter 716. Skipped for up-manga, whose URLs drop the decimal point (125.5 -> 1255):
  // pages that arrive without data-num (e.g. via Jina) fall through to the visible chapter text below.
  const escapedSlug = slug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!/up-manga\.com/i.test(sourceUrl)) {
    const slugChapterRegex = new RegExp(
      `(?:${escapedSlug}|/manga/${escapedSlug})[-_/]+(?:ตอนที่?|chapter|chap|ch|ep)?[-_/]?(\\d+(?:\\.\\d+)?)(?=[/"'?#&)\\s]|$)`,
      'gi'
    );
    while ((match = slugChapterRegex.exec(normalized)) !== null) {
      const val = parseFloat(match[1]);
      if (isSafeChapterNum(val) && val > maxCh) {
        maxCh = val;
      }
    }
    if (maxCh > 0) return maxCh;
  }

  // Pattern 2b: up-manga chapter labels anywhere on the page: <span class="chapternum">ตอนที่ 125</span>
  const chapterNumSpanRegex = /class=["']chapternum["'][^>]*>\s*(?:ตอนที่?|chapter|ch\.?|ep\.?)?\s*(\d+(?:\.\d+)?)/gi;
  while ((match = chapterNumSpanRegex.exec(normalized)) !== null) {
    const val = parseFloat(match[1]);
    if (isSafeChapterNum(val) && val > maxCh) {
      maxCh = val;
    }
  }
  if (maxCh > 0) return maxCh;

  // Pattern 3: Scoped to chapter list container: <span class="chapternum">ตอนที่ 155</span>
  if (chapterListMatch) {
    const classPattern =
      /<(?:span|li|div|a)\b[^>]*class=["'][^"']*(?:chapter|chapternum|epcur)[^"']*["'][^>]*>([\s\S]{0,120}?)<\/(?:span|li|div|a)>/gi;
    while ((match = classPattern.exec(searchArea)) !== null) {
      const innerText = match[1].replace(/<[^>]+>/g, ' ');
      const numMatch = innerText.match(/(?:ตอนที่?|chapter|ch\.?|ep\.?)?\s*(\d+(?:\.\d+)?)/i);
      if (numMatch) {
        const val = parseFloat(numMatch[1]);
        if (isSafeChapterNum(val) && val > maxCh) {
          maxCh = val;
        }
      }
    }
    if (maxCh > 0) return maxCh;
  }

  // Pattern 4: Markdown links from Jina proxy specifically containing the manga slug
  const mdRegex = new RegExp(
    `\\[(?:[^\\]]*?(?:ตอนที่?|chapter|ch|ep|ตอนใหม่ตอนที่?)\\s*(\\d+(?:\\.\\d+)?)[^\\]]*)\\]\\([^)]*${escapedSlug}[^)]*\\)`,
    'gi'
  );
  while ((match = mdRegex.exec(normalized)) !== null) {
    const val = parseFloat(match[1]);
    if (isSafeChapterNum(val) && val > maxCh) {
      maxCh = val;
    }
  }
  if (maxCh > 0) return maxCh;

  // Pattern 5: Direct "ตอนใหม่ ตอนที่ X" or "ตอนล่าสุด ตอนที่ X" buttons
  const latestBtnRegex = /(?:ตอนใหม่|ตอนล่าสุด)\s*(?:ตอนที่?)?\s*(\d+(?:\.\d+)?)/gi;
  while ((match = latestBtnRegex.exec(normalized)) !== null) {
    const val = parseFloat(match[1]);
    if (isSafeChapterNum(val) && val > maxCh) {
      maxCh = val;
    }
  }

  // Pattern 6: Markdown link list "[ตอนที่ 48 ...](...)" or "[ตอนใหม่ตอนที่ 48]"
  const mdListRegex = /\[(?:ตอนที่?|chapter|ch|ep|ตอนใหม่ตอนที่?)\s*(\d+(?:\.\d+)?)[^\]]*\]\(/gi;
  while ((match = mdListRegex.exec(normalized)) !== null) {
    const val = parseFloat(match[1]);
    if (isSafeChapterNum(val) && val > maxCh) {
      maxCh = val;
    }
  }

  return maxCh;
}

// Jina Reader allows 20 requests/minute per IP without an API key. Going over returns 429, which
// either fails the check or falls back to a stale cached page, so every client-side Jina call
// waits for a slot in a sliding 60s window instead.
const JINA_WINDOW_MS = 60000;
const JINA_MAX_PER_WINDOW = 18;
const jinaCallTimes: number[] = [];

async function acquireJinaSlot(): Promise<void> {
  for (;;) {
    const now = Date.now();
    while (jinaCallTimes.length > 0 && now - jinaCallTimes[0] >= JINA_WINDOW_MS) {
      jinaCallTimes.shift();
    }
    if (jinaCallTimes.length < JINA_MAX_PER_WINDOW) {
      jinaCallTimes.push(now);
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, jinaCallTimes[0] + JINA_WINDOW_MS - now + 50));
  }
}

async function fetchJina(jinaUrl: string, headers: Record<string, string>, timeoutMs: number): Promise<Response> {
  await acquireJinaSlot();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(jinaUrl, { signal: controller.signal, headers });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch a webpage with strict timeout, cache-busting, and smart proxy fallback
 */
export async function fetchPageContent(
  url: string,
  timeoutMs: number = 8000,
  forceFresh: boolean = true
): Promise<string> {
  const is1668 = url.includes('1668manga.com');
  const effectiveTimeout = is1668 ? Math.max(timeoutMs, 9500) : Math.max(timeoutMs, 6000);

  const isBlockedContent = (text: string): boolean => {
    // Turnstile markers alone are not a block: 1668manga embeds a Turnstile widget in the comment
    // form of every real series page. Only treat them as a challenge on small, content-less pages.
    const hasTurnstile = text.includes('challenges.cloudflare.com') || text.includes('cf-turnstile');
    return (
      text.includes('<title>Just a moment...</title>') ||
      text.includes('_cf_chl_opt') ||
      (hasTurnstile && text.length < 30000) ||
      text.includes('challenge-container') ||
      text.includes('token.awswaf.com') ||
      text.includes('awsWafCookieDomainList') ||
      text.includes('window.gokuProps') ||
      text.includes('RateLimitTriggeredError') ||
      text.includes('Per IP rate limit exceeded') ||
      text.includes('"status":429') ||
      text.includes('"code":429')
    );
  };

  // For 1668manga: Client-side Jina directly with X-Return-Format: html and X-No-Cache: true
  if (is1668) {
    const encoded = encodeURI(url);
    const cleanUrl = encoded.replace(/^https?:\/\//i, '');
    const jinaUrl = `https://r.jina.ai/https://${cleanUrl}`;

    try {
      const jinaHeaders: Record<string, string> = {
        'X-Return-Format': 'html',
      };
      if (forceFresh) {
        jinaHeaders['X-No-Cache'] = 'true';
      }
      const jinaRes = await fetchJina(jinaUrl, jinaHeaders, effectiveTimeout);

      if (jinaRes.ok) {
        const text = await jinaRes.text();
        if (text.length > 200 && !isBlockedContent(text)) return text;
      } else if (jinaRes.status === 429 && forceFresh) {
        // Rate limit when forcing fresh render; try reading Jina's cached HTML without X-No-Cache
        const cachedRes = await fetchJina(jinaUrl, { 'X-Return-Format': 'html' }, 5000);
        if (cachedRes.ok) {
          const text = await cachedRes.text();
          if (text.length > 200 && !isBlockedContent(text)) return text;
        }
      }
    } catch {
      // Fallback to proxy
    }
  }

  // Method 1: Try backend proxy (bypasses CORS and Cloudflare APO tcache for up-manga & mangablackcat)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);
    const proxyRes = await fetch(
      `/api/proxy?url=${encodeURIComponent(url)}&nocache=${forceFresh ? '1' : '0'}&_t=${Date.now()}`,
      {
        signal: controller.signal,
        cache: 'no-store',
      }
    );
    clearTimeout(timeoutId);
    if (proxyRes.ok) {
      const text = await proxyRes.text();
      if (text.length > 200 && !isBlockedContent(text)) {
        return text;
      }
    }
  } catch {
    // Proceed to fallback
  }

  // Method 2: Direct Jina Reader Proxy fallback (handles Cloudflare Turnstile & AWS WAF)
  if (!is1668) {
    try {
      const encoded = encodeURI(url);
      const cleanUrl = encoded.replace(/^https?:\/\//i, '');
      const jinaUrl = `https://r.jina.ai/https://${cleanUrl}`;
      const jinaHeaders: Record<string, string> = {
        'X-Return-Format': 'html',
      };
      if (forceFresh) {
        jinaHeaders['X-No-Cache'] = 'true';
      }
      const jinaRes = await fetchJina(jinaUrl, jinaHeaders, effectiveTimeout);

      if (jinaRes.ok) {
        const text = await jinaRes.text();
        if (text.length > 200 && !isBlockedContent(text)) return text;
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('หมดเวลาการเชื่อมต่อ (Timeout)');
      }
      throw err;
    }
  }

  throw new Error(`ไม่สามารถดึงข้อมูลเว็บ ${getSourceName(url)} ได้`);
}

/**
 * Check single source for latest chapter
 * If expectedTitle is provided, it verifies the page content title matches before accepting.
 */
export async function checkSourceChapter(
  source: MangaSource,
  timeoutMs: number = 8000,
  expectedTitle?: string
): Promise<ChapterCheckResult> {
  const name = source.name || getSourceName(source.url);

  // For mangablackcat.com: Auto-append ?sort=desc if missing so it fetches latest chapters instead of oldest chapters
  let targetUrl = source.url;
  if (targetUrl.includes('mangablackcat.com') && !targetUrl.includes('sort=')) {
    targetUrl = targetUrl.replace(/\/+$/, '') + '?sort=desc';
  }

  try {
    const content = await fetchPageContent(targetUrl, timeoutMs, true);
    const latestChapter = findLatestChapterFromPage(content, targetUrl);

    // Content Title Sanity Verification
    if (expectedTitle && expectedTitle.trim()) {
      const check = isPageTitleMatching(expectedTitle, content);
      if (!check.matches) {
        const lowerPageTitle = (check.pageTitle || '').toLowerCase();
        const isErrorOrBlockPage =
          lowerPageTitle.includes('404') ||
          lowerPageTitle.includes('403') ||
          lowerPageTitle.includes('500') ||
          lowerPageTitle.includes('502') ||
          lowerPageTitle.includes('503') ||
          lowerPageTitle.includes('504') ||
          lowerPageTitle.includes('520') ||
          lowerPageTitle.includes('521') ||
          lowerPageTitle.includes('522') ||
          lowerPageTitle.includes('524') ||
          lowerPageTitle.includes('not found') ||
          lowerPageTitle.includes('ไม่พบหน้า') ||
          lowerPageTitle.includes('error') ||
          lowerPageTitle.includes('just a moment') ||
          lowerPageTitle.includes('attention required') ||
          lowerPageTitle.includes('cloudflare') ||
          lowerPageTitle.includes('access denied') ||
          lowerPageTitle.includes('rate limit') ||
          lowerPageTitle.includes('too many requests') ||
          lowerPageTitle.includes('bad gateway') ||
          lowerPageTitle.includes('time-out') ||
          lowerPageTitle.includes('timeout') ||
          lowerPageTitle.includes('service unavailable');

        // Only mark as isMismatched if we found a real manga page (with chapters > 0) belonging to a different manga
        const isDefiniteMismatch =
          Boolean(check.pageTitle) &&
          check.pageTitle.trim().length >= 3 &&
          !isErrorOrBlockPage &&
          latestChapter > 0;

        return {
          sourceUrl: targetUrl,
          sourceName: name,
          latestChapter: 0,
          isMismatched: isDefiniteMismatch,
          pageTitle: check.pageTitle,
          error: isDefiniteMismatch
            ? `เนื้อหาในเว็บ (${check.pageTitle}) ไม่ตรงกับชื่อเรื่อง "${expectedTitle}"`
            : isErrorOrBlockPage
            ? `ไม่พบหน้าเว็บหรือเซิร์ฟเวอร์ขัดข้อง (${check.pageTitle})`
            : `ไม่สามารถยืนยันชื่อเรื่องได้`,
        };
      }
    }

    return {
      sourceUrl: targetUrl,
      sourceName: name,
      latestChapter,
      latestChapterAt: (latestChapter > 0 && findChapterReleaseAt(content, latestChapter)) || undefined,
      fromPage: latestChapter > 0,
    };
  } catch (err: any) {
    return {
      sourceUrl: targetUrl,
      sourceName: name,
      latestChapter: 0,
      error: err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล',
    };
  }
}

export interface DiscoveredSource {
  url: string;
  chapter: number;
  coverUrl?: string;
}

export function cleanTitleForSearch(title: string): string {
  return title
    .replace(/[\ufffd\uFFFD]/g, "'")
    .replace(/[:\-–—!?,’']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Dedicated high-speed search for up-manga.com using their native WordPress Ajaxy Live Search API.
 * Single-shot direct query in ~200ms.
 */
export async function searchUpManga(title: string): Promise<DiscoveredSource | null> {
  try {
    const cleanQ = cleanTitleForSearch(title);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const res = await fetch(`/api/search-upmanga?q=${encodeURIComponent(cleanQ)}`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const results: Array<{ title: string; link: string; latest: number; image?: string }> = await res.json();
      let bestItem: DiscoveredSource | null = null;
      let bestScore = 0;

      for (const item of results) {
        if (!item.link) continue;
        const candSlug = extractSlug(item.link) || '';
        const matchInfo = calculateTitleMatchScore(title, item.title || '', candSlug);
        if (matchInfo.score >= 0.45 && matchInfo.score > bestScore) {
          bestScore = matchInfo.score;
          bestItem = {
            url: normalizeToMangaUrl(item.link) || item.link,
            chapter: item.latest || 0,
            coverUrl: item.image || undefined,
          };
        }
      }

      if (bestItem) {
        if (bestItem.chapter === 0) {
          try {
            const content = await fetchPageContent(bestItem.url, 4000);
            bestItem.chapter = findLatestChapterFromPage(content, bestItem.url);
          } catch {}
        }
        return bestItem;
      }
    }
  } catch {}
  return null;
}

/**
 * Dedicated high-speed search for mangablackcat.com (sub-300ms)
 * Single-shot direct query. Returns URL, title, and latest chapter.
 */
export async function searchMangaBlackCat(title: string): Promise<DiscoveredSource | null> {
  const cleanQ = cleanTitleForSearch(title);

  // 1. Dedicated endpoint /api/search-mangablackcat (3000ms timeout)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`/api/search-mangablackcat?q=${encodeURIComponent(cleanQ)}`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const results: Array<{ title: string; link: string; latest: number }> = await res.json();
      let bestItem: DiscoveredSource | null = null;
      let bestScore = 0;

      for (const item of results) {
        if (!item.link) continue;
        const candSlug = extractSlug(item.link) || '';
        const matchInfo = calculateTitleMatchScore(title, item.title || '', candSlug);
        if (matchInfo.score >= 0.55 && matchInfo.score > bestScore) {
          bestScore = matchInfo.score;
          bestItem = {
            url: item.link.includes('sort=') ? item.link : `${item.link.replace(/\/+$/, '')}?sort=desc`,
            chapter: item.latest || 0,
          };
        }
      }
      return bestItem;
    }
  } catch {}

  // 2. Direct fallback via /api/proxy ONLY if primary endpoint threw network error
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(
      `/api/proxy?url=${encodeURIComponent(`https://mangablackcat.com/search?q=${encodeURIComponent(cleanQ)}`)}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (res.ok) {
      const html = await res.text();
      const itemRegex = /<a\s+[^>]*href=["'](https:\/\/mangablackcat\.com\/manga\/[^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
      let m: RegExpExecArray | null;
      let bestItem: DiscoveredSource | null = null;
      let bestScore = 0;

      while ((m = itemRegex.exec(html)) !== null) {
        const url = m[1];
        const candSlug = extractSlug(url) || '';
        const text = m[2].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        const chMatch = text.match(/(\d+(?:\.\d+)?)\s*ตอน/i) || text.match(/ตอนที่?\s*(\d+(?:\.\d+)?)/i);
        const chapter = chMatch ? parseFloat(chMatch[1]) : 0;
        const cleanTitle = text.replace(/^\d+(?:\.\d+)?\s*ตอน\s*/i, '').replace(/ตอนที่?\s*\d+(?:\.\d+)?/i, '').trim();

        const scoreInfo = calculateTitleMatchScore(title, cleanTitle, candSlug);
        if (scoreInfo.score >= 0.55 && scoreInfo.score > bestScore) {
          bestScore = scoreInfo.score;
          bestItem = {
            url: url.replace(/\/+$/, '') + '?sort=desc',
            chapter,
          };
        }
      }
      return bestItem;
    }
  } catch {}

  return null;
}

/**
 * Dedicated high-speed search for 1668manga.com
 * Single-shot direct query. Returns URL, title, latest chapter, and cover image.
 */
export async function search1668Manga(title: string): Promise<DiscoveredSource | null> {
  const cleanQ = cleanTitleForSearch(title);

  // 1. Dedicated endpoint /api/search-1668 (7000ms timeout)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);
    const res = await fetch(`/api/search-1668?q=${encodeURIComponent(cleanQ)}`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const results: Array<{ title: string; link: string; latest: number; image?: string }> = await res.json();
      let bestItem: DiscoveredSource | null = null;
      let bestScore = 0;

      for (const item of results) {
        if (!item.link) continue;
        const candSlug = extractSlug(item.link) || '';
        const matchInfo = calculateTitleMatchScore(title, item.title || '', candSlug);
        if (matchInfo.score >= 0.55 && matchInfo.score > bestScore) {
          bestScore = matchInfo.score;
          bestItem = {
            url: normalizeToMangaUrl(item.link) || item.link,
            chapter: item.latest || 0,
            coverUrl: item.image || undefined,
          };
        }
      }
      if (bestItem) return bestItem;
    }
  } catch {}

  // 2. Canonical slug direct lookup fallback
  try {
    const slug = cleanQ
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');

    if (slug.length >= 3) {
      const testUrl = `https://1668manga.com/manga/${slug}/`;
      const res = await fetchJina(`https://r.jina.ai/${testUrl}`, { 'X-Return-Format': 'html' }, 8000);

      if (res.ok) {
        const text = await res.text();
        if (
          text &&
          text.length > 200 &&
          !text.includes('Page not found') &&
          !text.includes('404')
        ) {
          const matchCheck = isPageTitleMatching(title, text);
          if (matchCheck.matches) {
            const chapter = findLatestChapterFromPage(text, testUrl);
            return { url: testUrl, chapter };
          }
        }
      }
    }
  } catch {}

  return null;
}

/**
 * Auto-discover a manga page URL on a specific site (e.g. up-manga.com, 1668manga.com, mangablackcat.com)
 * High-speed direct API dispatch with zero redundant requests.
 */
export async function autoDiscoverMangaOnSite(
  site: KnownSite,
  title: string
): Promise<DiscoveredSource | null> {
  const siteDomain = getSourceName(site.url).toLowerCase();
  if (!siteDomain || !title.trim()) return null;

  // 1. Up-Manga (Live Search API - ~200ms)
  if (siteDomain.includes('up-manga.com')) {
    return await searchUpManga(title);
  }

  // 2. MangaBlackCat (Live Search API - ~300ms)
  if (siteDomain.includes('mangablackcat.com')) {
    return await searchMangaBlackCat(title);
  }

  // 3. 1668Manga (Search API - ~1-2s)
  if (siteDomain.includes('1668manga.com')) {
    return await search1668Manga(title);
  }

  // 4. Custom/Generic site handling (fast single slug check, timeout 4000ms)
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9ก-๙\s-]/gi, '')
    .trim()
    .replace(/\s+/g, '-');

  const testUrl = `${site.url.replace(/\/+$/, '')}/manga/${slug}/`;
  try {
    const pageContent = await fetchPageContent(testUrl, 4000);
    if (
      pageContent &&
      pageContent.length > 200 &&
      !pageContent.includes('Page not found') &&
      !pageContent.includes('404 - ไม่พบหน้าที่ค้นหา') &&
      !pageContent.includes('<title>404')
    ) {
      const check = isPageTitleMatching(title, pageContent);
      if (check.matches) {
        const chapter = findLatestChapterFromPage(pageContent, testUrl);
        return { url: testUrl, chapter };
      }
    }
  } catch {}

  return null;
}

// --------------------------------------------------------------------------
// Live Recent-Updates Feed Preloader (1668manga.com & up-manga.com)
// Solves:
// 1) 1668manga being skipped when up-manga/mangablackcat haven't updated yet (noon vs evening lag)
// 2) Jina Reader 20 RPM rate-limit when checking 75 manga
// 3) Stale Jina Reader / LiteSpeed caches on individual manga pages
// --------------------------------------------------------------------------
let recent1668FeedMap = new Map<string, number>();
let recentUpMangaFeedMap = new Map<string, number>();
// slug -> epoch ms the feed chapter was published (1668manga homepage lists relative times)
let recent1668FeedAtMap = new Map<string, number>();
// True when the 1668manga feed reached back to the previous successful sync, i.e. a series that is
// missing from the feed has had no new chapter since then and its stored chapter can be trusted.
let recent1668FeedComplete = false;
let lastFeedLoadedAt = 0;
let activeFeedPromise: Promise<void> | null = null;

const FEED_1668_SYNCED_AT_KEY = 'manga_1668_feed_synced_at';
const FEED_1668_MIN_COVERAGE_MS = 2 * 24 * 60 * 60 * 1000;
const FEED_1668_FIRST_RUN_COVERAGE_MS = 4 * 24 * 60 * 60 * 1000;
const FEED_1668_SYNC_MARGIN_MS = 12 * 60 * 60 * 1000;
const FEED_1668_MAX_PAGES = 15;
const FEED_1668_PAGES_PER_WAVE = 3;

const RELATIVE_TIME_UNIT_MS: Record<string, number> = {
  second: 1000,
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
  year: 365 * 24 * 60 * 60 * 1000,
};

const RELATIVE_UNIT_ALIASES: Record<string, string> = {
  s: 'second', sec: 'second', secs: 'second', วินาที: 'second',
  m: 'minute', min: 'minute', mins: 'minute', นาที: 'minute',
  h: 'hour', hr: 'hour', hrs: 'hour', ชั่วโมง: 'hour', 'ชม.': 'hour', ชม: 'hour',
  d: 'day', วัน: 'day',
  w: 'week', สัปดาห์: 'week', อาทิตย์: 'week',
  mo: 'month', เดือน: 'month',
  y: 'year', yr: 'year', ปี: 'year',
};

/** "11 minutes ago" / "1w ago" / "3 วันที่แล้ว" -> age in ms */
function parseRelativeAgeMs(text: string): number | null {
  const en = text.match(/(\d+)\s*(second|minute|hour|day|week|month|year|secs?|mins?|hrs?|yr|mo|s|m|h|d|w|y)s?\s+ago/i);
  if (en) {
    const unit = en[2].toLowerCase();
    const canonical = RELATIVE_TIME_UNIT_MS[unit] ? unit : RELATIVE_UNIT_ALIASES[unit];
    return canonical ? parseInt(en[1], 10) * RELATIVE_TIME_UNIT_MS[canonical] : null;
  }
  const th = text.match(/(\d+)\s*(วินาที|นาที|ชั่วโมง|ชม\.?|วัน|สัปดาห์|อาทิตย์|เดือน|ปี)\s*(?:ที่แล้ว|ก่อน)/);
  if (th) {
    return parseInt(th[1], 10) * RELATIVE_TIME_UNIT_MS[RELATIVE_UNIT_ALIASES[th[2]]];
  }
  return null;
}

const MONTH_INDEX: Record<string, number> = {
  มกราคม: 0, กุมภาพันธ์: 1, มีนาคม: 2, เมษายน: 3, พฤษภาคม: 4, มิถุนายน: 5,
  กรกฎาคม: 6, สิงหาคม: 7, กันยายน: 8, ตุลาคม: 9, พฤศจิกายน: 10, ธันวาคม: 11,
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

/**
 * Chapter release text -> epoch ms. Handles "ตุลาคม 23, 2026" / "October 23, 2026" (local midnight,
 * Buddhist years too) and relative ages like "26 seconds ago" or "1w ago".
 */
export function parseChapterDate(text: string, now: number = Date.now()): number | null {
  if (!text) return null;
  const clean = text.replace(/\s+/g, ' ').trim();

  const age = parseRelativeAgeMs(clean);
  if (age !== null) return now - age;

  const abs = clean.match(/([ก-๙a-z]+)\s+(\d{1,2}),?\s+(\d{4})/i);
  if (abs) {
    const month = MONTH_INDEX[abs[1].toLowerCase()];
    let year = parseInt(abs[3], 10);
    if (year > 2400) year -= 543;
    if (month !== undefined) {
      const ts = new Date(year, month, parseInt(abs[2], 10)).getTime();
      // A future date is a parsing mistake (or a timezone edge); never show "in 3 days"
      return isNaN(ts) ? null : Math.min(ts, now);
    }
  }
  return null;
}

/**
 * Release time of `chapter` (or the newest listed chapter) from a series page's chapter list.
 * MangaThemesia (up-manga, 1668manga): <li data-num="335"> ... <span class="chapterdate">ตุลาคม 23, 2026</span>
 * mangablackcat: <a data-chapter-number="334"> ... <p>1w ago</p>
 */
export function findChapterReleaseAt(content: string, chapter: number, now: number = Date.now()): number | null {
  if (!content) return null;
  const entries: { num: number; text: string }[] = [];
  const patterns = [
    /data-num=["'](\d+(?:\.\d+)?)["'][\s\S]{0,700}?class=["']chapterdate["'][^>]*>([^<]+)</gi,
    /data-chapter-number=["'](\d+(?:\.\d+)?)["'][\s\S]{0,3000}?<p\b[^>]*>\s*([^<]*?\bago)\s*<\/p>/gi,
  ];
  for (const re of patterns) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      entries.push({ num: parseFloat(m[1]), text: m[2] });
    }
    if (entries.length > 0) break;
  }
  if (entries.length === 0) return null;

  // A date for any other chapter would mislabel the release time, so only the exact entry counts
  const hit = entries.find((e) => e.num === chapter);
  return hit ? parseChapterDate(hit.text, now) : null;
}

function readFeedSyncedAt(): number {
  try {
    return Number(localStorage.getItem(FEED_1668_SYNCED_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeFeedSyncedAt(ts: number) {
  try {
    localStorage.setItem(FEED_1668_SYNCED_AT_KEY, String(ts));
  } catch {}
}

const isFeedChapterNum = (ch: number) => !isNaN(ch) && ch > 0 && ch < 5000 && !(ch >= 2020 && ch <= 2030);

/**
 * Parse a MangaThemesia listing page (homepage "Latest Update" .uta blocks or ?order=update .bsx blocks).
 * Returns how far back the page reaches (boundaryAgeMs), so callers know when the feed has covered enough time.
 */
export function parseMangaThemesiaUpdateHtml(
  html: string,
  domainKeyword: string,
  targetMap: Map<string, number>,
  /** Optional slug -> epoch ms the newest feed chapter was published */
  releaseAtMap?: Map<string, number>
): { boundaryAgeMs: number | null } {
  const now = Date.now();
  // Age of each block's newest chapter, in page order
  const blockAges: number[] = [];
  if (!html) return { boundaryAgeMs: null };
  const decoded = html.replace(/(?:%[0-9a-fA-F]{2})+/g, (seq) => {
    try {
      return decodeURIComponent(seq);
    } catch {
      return seq;
    }
  });

  // 1. Split into one chunk per series block. Each chunk is parsed on its own so a block without a
  // chapter (e.g. a brand-new series) can never borrow the chapter number of the block after it.
  const blocks = decoded.split(/<div class=["'](?:bsx|uta)["']>/i).slice(1);
  for (const block of blocks) {
    const urlMatch = block.match(/href=["'](https?:\/\/[^"']+)["']/i);
    if (!urlMatch || !urlMatch[1].toLowerCase().includes(domainKeyword)) continue;
    const slug = extractSlug(urlMatch[1])?.toLowerCase();
    if (!slug) continue;

    let ch = 0;
    let newestAgeMs: number | null = null;
    // .bsx: <div class="epxs">ตอนที่ 41</div> inside the card's own <a>...</a>
    const cardEnd = block.indexOf('</a>');
    const card = cardEnd >= 0 ? block.slice(0, cardEnd) : block;
    const epxsMatch = card.match(/<div class=["']epxs["'][^>]*>\s*(?:ตอนที่?|chapter|ch\.?|ep\.?)?\s*(\d+(?:\.\d+)?)/i);
    if (epxsMatch) ch = parseFloat(epxsMatch[1]);

    // .uta: <ul><li><a href="...">ตอนที่ 53</a><span>11 minutes ago</span></li>...</ul> (newest first)
    const listMatch = block.match(/<ul\b[^>]*>([\s\S]*?)<\/ul>/i);
    if (listMatch) {
      const liRegex =
        /<li>\s*<a[^>]*>\s*(?:ตอนที่?|chapter|ch\.?|ep\.?)?\s*(\d+(?:\.\d+)?)[^<]*<\/a>\s*(?:<span>([^<]*)<\/span>)?/gi;
      let li: RegExpExecArray | null;
      let isFirst = true;
      while ((li = liRegex.exec(listMatch[1])) !== null) {
        const liCh = parseFloat(li[1]);
        if (isFeedChapterNum(liCh) && liCh > ch) ch = liCh;
        if (isFirst && li[2]) {
          const age = parseRelativeAgeMs(li[2]);
          if (age !== null) {
            blockAges.push(age);
            if (liCh === ch) newestAgeMs = age;
          }
        }
        isFirst = false;
      }
    }

    if (isFeedChapterNum(ch)) {
      const prev = targetMap.get(slug) || 0;
      if (ch > prev) {
        targetMap.set(slug, ch);
        if (releaseAtMap) {
          if (newestAgeMs !== null) releaseAtMap.set(slug, now - newestAgeMs);
          else releaseAtMap.delete(slug);
        }
      }
    }
  }

  // 2. Fallback: direct chapter links e.g. https://1668manga.com/slug-ตอนที่-160/ for series no block covered.
  // 1668manga only: up-manga drops the decimal point from chapter URLs (125.5 -> ตอนที่-1255).
  const chLinkRegex =
    /href=["']https?:\/\/(?:www\.)?1668manga\.com\/(?:manga\/)?([^"'\/?#]+?)-(?:ตอนที่|chapter|ep|ch)-(\d+(?:\.\d+)?)\/?["']/gi;
  const linkMax = new Map<string, number>();
  let m: RegExpExecArray | null;
  while ((m = chLinkRegex.exec(decoded)) !== null) {
    const slug = m[1].toLowerCase();
    const ch = parseFloat(m[2]);
    if (slug && isFeedChapterNum(ch) && ch > (linkMax.get(slug) || 0)) linkMax.set(slug, ch);
  }
  for (const [slug, ch] of linkMax) {
    if (!targetMap.has(slug)) targetMap.set(slug, ch);
  }

  // The list is sorted by last update, so the page ends where its last items are. Use the median of
  // the last 5 so one series bumped to the top with an old chapter ("2 weeks ago") can't fake coverage.
  if (blockAges.length === 0) return { boundaryAgeMs: null };
  const tail = blockAges.slice(-5).sort((a, b) => a - b);
  return { boundaryAgeMs: tail[Math.floor(tail.length / 2)] };
}

export async function fetch1668ListingHtml(targetUrl: string): Promise<string> {
  const jinaUrl = `https://r.jina.ai/https://${targetUrl.replace(/^https?:\/\//i, '')}`;
  try {
    const res = await fetchJina(jinaUrl, { 'X-Return-Format': 'html', 'X-No-Cache': 'true' }, 12000);
    if (res.ok) return await res.text();
  } catch {}
  return '';
}

/**
 * Walk 1668manga's homepage "Latest Update" pages (/page/N/) until the timestamps reach back to the
 * previous sync, so a series updated since then is always seen, however long ago the last check was.
 * Returns true when that coverage was reached.
 */
async function load1668Feed(targetMap: Map<string, number>, releaseAtMap: Map<string, number>): Promise<boolean> {
  const startedAt = Date.now();
  const syncedAt = readFeedSyncedAt();
  const neededAgeMs =
    syncedAt > 0
      ? Math.max(FEED_1668_MIN_COVERAGE_MS, startedAt - syncedAt + FEED_1668_SYNC_MARGIN_MS)
      : FEED_1668_FIRST_RUN_COVERAGE_MS;

  let reachedAgeMs = 0;
  let loadedPages = 0;
  for (let page = 1; page <= FEED_1668_MAX_PAGES; page += FEED_1668_PAGES_PER_WAVE) {
    const pages = Array.from(
      { length: Math.min(FEED_1668_PAGES_PER_WAVE, FEED_1668_MAX_PAGES - page + 1) },
      (_, i) => page + i
    );
    const htmls = await Promise.all(
      pages.map((p) => fetch1668ListingHtml(p === 1 ? 'https://1668manga.com/' : `https://1668manga.com/page/${p}/`))
    );
    let waveFailed = false;
    for (const html of htmls) {
      const { boundaryAgeMs } = parseMangaThemesiaUpdateHtml(html, '1668manga.com', targetMap, releaseAtMap);
      if (boundaryAgeMs === null) {
        waveFailed = true;
        continue;
      }
      loadedPages++;
      reachedAgeMs = Math.max(reachedAgeMs, boundaryAgeMs);
    }
    if (reachedAgeMs >= neededAgeMs) break;
    // A page that failed to load leaves a hole in the timeline; stop and report incomplete coverage
    if (waveFailed) break;
  }

  const complete = loadedPages > 0 && reachedAgeMs >= neededAgeMs;
  if (complete) writeFeedSyncedAt(startedAt);
  return complete;
}

export async function preloadRecentUpdatesFeed(force: boolean = false): Promise<void> {
  const now = Date.now();
  if (!force && lastFeedLoadedAt > 0 && now - lastFeedLoadedAt < 25000 && recent1668FeedMap.size > 0) {
    return;
  }
  if (activeFeedPromise) {
    return activeFeedPromise;
  }

  activeFeedPromise = (async () => {
    const new1668Map = new Map<string, number>();
    const new1668AtMap = new Map<string, number>();
    const newUpMap = new Map<string, number>();

    const fetchUpMangaPage = async (targetUrl: string) => {
      try {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 5000);
        const res = await fetch(
          `/api/proxy?url=${encodeURIComponent(targetUrl)}&nocache=1&_t=${Date.now()}`,
          { signal: controller.signal, cache: 'no-store' }
        );
        clearTimeout(tid);
        if (res.ok) {
          const html = await res.text();
          parseMangaThemesiaUpdateHtml(html, 'up-manga.com', newUpMap);
        }
      } catch {}
    };

    const [complete1668] = await Promise.all([
      load1668Feed(new1668Map, new1668AtMap),
      // up-manga: Pages 1..3 of ?order=update (covers 60 most recently updated manga)
      fetchUpMangaPage('https://www.up-manga.com/manga/?order=update'),
      fetchUpMangaPage('https://www.up-manga.com/manga/?page=2&order=update'),
      fetchUpMangaPage('https://www.up-manga.com/manga/?page=3&order=update'),
    ]);

    if (new1668Map.size > 0) {
      recent1668FeedMap = new1668Map;
      recent1668FeedAtMap = new1668AtMap;
    }
    recent1668FeedComplete = complete1668;
    if (newUpMap.size > 0) {
      recentUpMangaFeedMap = newUpMap;
    }
    lastFeedLoadedAt = Date.now();
  })().finally(() => {
    activeFeedPromise = null;
  });

  return activeFeedPromise;
}

/**
 * Realtime poll: read only the first page of each site's latest-updates feed (1 proxy request +
 * 1 Jina request) and return the ids of tracked manga that the feed shows a newer chapter for.
 * Those few are then checked directly; everything else waits for the periodic full check.
 */
export async function findMangaWithFeedUpdates(mangaList: Manga[]): Promise<string[]> {
  const upMap = new Map<string, number>();
  const map1668 = new Map<string, number>();
  const at1668 = new Map<string, number>();

  const loadUp = async () => {
    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 6000);
      const res = await fetch(
        `/api/proxy?url=${encodeURIComponent('https://www.up-manga.com/manga/?order=update')}&nocache=1&_t=${Date.now()}`,
        { signal: controller.signal, cache: 'no-store' }
      );
      clearTimeout(tid);
      if (res.ok) parseMangaThemesiaUpdateHtml(await res.text(), 'up-manga.com', upMap);
    } catch {}
  };
  const load1668 = async () => {
    parseMangaThemesiaUpdateHtml(await fetch1668ListingHtml('https://1668manga.com/'), '1668manga.com', map1668, at1668);
  };
  await Promise.all([loadUp(), load1668()]);

  // Feed the batch-check caches too, so the follow-up checks never read an older chapter
  for (const [slug, ch] of upMap) {
    if (ch > (recentUpMangaFeedMap.get(slug) || 0)) recentUpMangaFeedMap.set(slug, ch);
  }
  for (const [slug, ch] of map1668) {
    if (ch > (recent1668FeedMap.get(slug) || 0)) {
      recent1668FeedMap.set(slug, ch);
      const at = at1668.get(slug);
      if (at) recent1668FeedAtMap.set(slug, at);
    }
  }

  const ids: string[] = [];
  for (const manga of mangaList) {
    const hasNewer = (manga.sources || []).some((src) => {
      const slug = extractSlug(src.url)?.toLowerCase();
      if (!slug) return false;
      const feedCh = src.url.includes('1668manga.com')
        ? map1668.get(slug) || 0
        : src.url.includes('up-manga.com')
        ? upMap.get(slug) || 0
        : 0;
      return feedCh > Math.max(src.chapter || 0, Number(manga.currentCh) || 0);
    });
    if (hasNewer) ids.push(manga.id);
  }
  return ids;
}

function earliestTimestamp(a?: number, b?: number): number | undefined {
  if (a && b) return Math.min(a, b);
  return a || b || undefined;
}

/**
 * Shared Chapter Check for a single Manga
 * Used by BOTH the Card Modal and the Main Top Button!
 */
export async function checkMangaSources(
  manga: Manga,
  onLog?: (msg: string) => void,
  /** direct: fetch every source page instead of trusting the update feeds (used for feed-triggered realtime checks) */
  options: { direct?: boolean } = {}
): Promise<{ updatedManga: Manga; hasUpdate: boolean; hasChanges: boolean }> {
  let maxCh = Number(manga.currentCh) || 0;
  let bestSource = manga.sources?.[0];
  const updatedSources = [...(manga.sources || [])];
  let hasUpdate = false;
  let hasChanges = false;
  const prevCh = Number(manga.currentCh) || 0;
  const todayStr = getTodayKey();
  const isSingleInteractiveCheck = Boolean(onLog) || Boolean(options.direct);

  onLog?.('เริ่มตรวจสอบแหล่งข้อมูล...');

  // Ensure recent updates feed is warm for batch checks
  if (!isSingleInteractiveCheck) {
    await preloadRecentUpdatesFeed(false);
  }

  const isSlowSource = (s: MangaSource) => Boolean(s.url && s.url.includes('1668manga.com'));
  const fastSources = updatedSources.filter((s) => !isSlowSource(s));
  const slowSources = updatedSources.filter(isSlowSource);

  const results: { src: MangaSource; res: ChapterCheckResult }[] = [];

  // 1. Check fast direct sources (up-manga, mangablackcat) in parallel
  if (fastSources.length > 0) {
    const fastResults = await Promise.all(
      fastSources.map(async (src) => {
        onLog?.(`กำลังดึงข้อมูลจาก ${src.name || src.url}...`);
        const res = await checkSourceChapter(src, 6500, manga.title);

        // If up-manga's live ?order=update feed has an even newer chapter than the cached series page, use it
        if (src.url.includes('up-manga.com')) {
          const slug = extractSlug(src.url)?.toLowerCase();
          const feedCh = slug ? recentUpMangaFeedMap.get(slug) || 0 : 0;
          if (feedCh > res.latestChapter) {
            res.latestChapter = feedCh;
            res.latestChapterAt = undefined;
            res.error = undefined;
          }
        }
        return { src, res };
      })
    );
    results.push(...fastResults);
  }

  const fastMaxChapter = results.reduce((m, r) => Math.max(m, r.res.latestChapter || 0), 0);

  // 2. Check 1668manga.com sources (NEVER skip 1668manga when fast sources are behind!)
  if (slowSources.length > 0) {
    const slowResults = await Promise.all(
      slowSources.map(async (src) => {
        const slug = extractSlug(src.url)?.toLowerCase();
        const feedCh = slug ? recent1668FeedMap.get(slug) || 0 : 0;

        // Perform direct page fetch if:
        // - User clicked "เช็คตอน" inside the single-manga modal (isSingleInteractiveCheck)
        // - Or recent1668FeedMap failed to load
        // - Or the feed could not reach back to the last sync and this series is not in it
        //   (it may have updated inside the uncovered gap)
        // - Or manga has no working fast sources and stored chapter is 0
        // Also once per session when the card shows no release time at all and this site holds its newest
        // chapter (e.g. added from Discover), so the card can show when that chapter came out
        const needsDate =
          !getLatestReleaseAt(manga) &&
          !src.chapterAt &&
          (src.chapter || 0) > 0 &&
          (src.chapter || 0) >= (Number(manga.currentCh) || 0) &&
          !datedThisSession.has(src.url) &&
          takeDateLookupSlot();
        if (needsDate) datedThisSession.add(src.url);
        const needDirectFetch =
          isSingleInteractiveCheck ||
          needsDate ||
          recent1668FeedMap.size === 0 ||
          (!recent1668FeedComplete && feedCh === 0) ||
          ((fastSources.length === 0 || fastMaxChapter === 0) && feedCh === 0 && (src.chapter || 0) === 0);

        if (needDirectFetch) {
          onLog?.(`กำลังดึงข้อมูลจาก ${src.name || src.url}...`);
          const res = await checkSourceChapter(src, 9500, manga.title);
          if (feedCh > res.latestChapter) {
            res.latestChapter = feedCh;
            res.latestChapterAt = undefined;
            res.error = undefined;
          }
          if (feedCh > 0 && feedCh === res.latestChapter) {
            // The homepage feed's relative time is more precise than the series page's calendar date
            res.latestChapterAt = (slug && recent1668FeedAtMap.get(slug)) || res.latestChapterAt;
          }
          return { src, res };
        }

        // Batch check: Use live 1668manga update feed if present, otherwise retain existing chapter
        const best1668Ch = Math.max(feedCh, src.chapter || 0);
        return {
          src,
          res: {
            sourceUrl: src.url,
            sourceName: src.name || getSourceName(src.url),
            latestChapter: best1668Ch,
            latestChapterAt: feedCh > 0 && feedCh === best1668Ch && slug ? recent1668FeedAtMap.get(slug) : undefined,
          },
        };
      })
    );
    results.push(...slowResults);
  }

  const validSources: MangaSource[] = [];

  for (const { src, res } of results) {
    if (res.isMismatched) {
      onLog?.(
        `❌ ${res.sourceName}: เนื้อหาในเว็บไม่ตรงกับเรื่อง! (พบ "${res.pageTitle || 'คนละเรื่อง'}") ➔ ลบลิงก์นี้ออกให้อัตโนมัติ`
      );
      hasChanges = true;
      continue;
    }

    if (res.latestChapter > 0) {
      const prevSrcCh = src.chapter || 0;
      // The site's own page is the truth: its top chapter is the chapter, even when lower than what was stored
      // (e.g. a chapter count once taken for a chapter, or a removed chapter). Values not read from a page
      // (1668 batch checks without a feed hit) only keep what was stored.
      const finalSrcCh = res.fromPage ? res.latestChapter : Math.max(res.latestChapter, prevSrcCh);
      const resAt = res.latestChapter === finalSrcCh ? res.latestChapterAt : undefined;
      if (finalSrcCh < prevSrcCh) {
        onLog?.(`↺ ${res.sourceName}: ตอนบนสุดในเว็บคือ ${finalSrcCh} (เดิมบันทึกไว้ ${prevSrcCh}) ➔ ใช้ตามเว็บ`);
      }
      // A new chapter without a listed date is stamped when it was noticed; realtime polling keeps that
      // close to the real release. Not on a first check (prevSrcCh 0), where the chapter could be years old.
      const chapterAt =
        finalSrcCh > prevSrcCh
          ? resAt || (prevSrcCh > 0 ? Date.now() : undefined)
          : finalSrcCh < prevSrcCh
          ? resAt
          : earliestTimestamp(src.chapterAt, resAt);
      if (
        finalSrcCh !== src.chapter ||
        chapterAt !== src.chapterAt ||
        (res.sourceUrl && res.sourceUrl !== src.url)
      ) {
        hasChanges = true;
      }
      const checkedAt = res.fromPage ? Date.now() : src.checkedAt;
      const updatedSrc = { ...src, url: res.sourceUrl || src.url, chapter: finalSrcCh, chapterAt, checkedAt };
      validSources.push(updatedSrc);
      onLog?.(`✓ ${res.sourceName}: พบตอนที่ ${finalSrcCh}`);
      if (finalSrcCh > maxCh) {
        maxCh = finalSrcCh;
        bestSource = updatedSrc;
        hasUpdate = true;
      }
    } else {
      onLog?.(`⚠ ${res.sourceName}: ${res.error || 'หาเลขตอนไม่พบ'}`);
      // Preserve existing source chapter if available! Don't let a failed check leave source chapter as 0
      const preservedCh = src.chapter || (Number(manga.currentCh) || 0);
      validSources.push({ ...src, url: res.sourceUrl || src.url, chapter: preservedCh });
    }
  }

  // Re-evaluate maxCh strictly from remaining valid sources
  if (validSources.length > 0) {
    let validMax = 0;
    for (const s of validSources) {
      if ((s.chapter || 0) > validMax) {
        validMax = s.chapter || 0;
        bestSource = s;
      }
    }
    if (validMax > 0) {
      maxCh = validMax;
    } else if (maxCh === 0) {
      maxCh = Number(manga.currentCh) || Number(manga.readCh) || 0;
      if (validSources[0] && !validSources[0].chapter && maxCh > 0) {
        validSources[0].chapter = maxCh;
      }
    }
  } else {
    maxCh = Number(manga.readCh) || 0;
    bestSource = undefined as any;
  }

  const finalCh = maxCh > 0 ? maxCh : (Number(manga.readCh) || 0);
  // Release time on the fastest site: the earliest time any site published the newest chapter
  const fastestAt = validSources
    .filter((s) => (s.chapter || 0) === finalCh)
    .reduce<number | undefined>((acc, s) => earliestTimestamp(acc, s.chapterAt), undefined);
  const latestChapterAt =
    finalCh === prevCh
      ? earliestTimestamp(manga.latestChapterAt, fastestAt)
      : fastestAt || (prevCh > 0 ? Date.now() : undefined);
  if (latestChapterAt !== manga.latestChapterAt) hasChanges = true;

  const updatedManga: Manga = {
    ...manga,
    currentCh: finalCh,
    sources: validSources,
    lastUpdatedAt: hasUpdate || maxCh > prevCh ? todayStr : manga.lastUpdatedAt,
    latestChapterAt,
    latestSourceUrl: bestSource?.url || validSources[0]?.url || manga.latestSourceUrl,
    latestSourceName: bestSource?.name || validSources[0]?.name || manga.latestSourceName,
  };

  onLog?.(`เสร็จสิ้น! ตอนล่าสุดคือตอนที่ ${updatedManga.currentCh}`);
  return { updatedManga, hasUpdate: hasUpdate || maxCh > prevCh, hasChanges: hasChanges || maxCh !== prevCh };
}

/**
 * Shared Auto-Discover Missing Sources for a single Manga
 * Used by BOTH the Card Modal and the Main Top Button!
 */
export async function autoDiscoverMangaSources(
  manga: Manga,
  knownSites: KnownSite[],
  onLog?: (msg: string) => void
): Promise<{ updatedManga: Manga; discoveredCount: number }> {
  const currentSources = [...(manga.sources || [])];
  let discoveredCount = 0;
  let highestCh = Number(manga.currentCh) || 0;
  let hasUpdate = false;
  const todayStr = getTodayKey();

  onLog?.('เริ่มค้นหาลิงก์จากเว็บต่างๆ ที่ยังไม่มี...');

  const missingSites = knownSites.filter((site) => {
    const siteHost = getSourceName(site.url).toLowerCase();
    return !currentSources.some(
      (s) => getSourceName(s.url).toLowerCase() === siteHost
    );
  });

  if (missingSites.length === 0) {
    return { updatedManga: manga, discoveredCount: 0 };
  }

  onLog?.(`กำลังค้นหาใน ${missingSites.map((s) => s.name).join(', ')}...`);

  // Search missing sites concurrently for this manga
  const results = await Promise.all(
    missingSites.map(async (site) => {
      try {
        const found = await autoDiscoverMangaOnSite(site, manga.title);
        if (found && found.url) {
          let foundCh = found.chapter || 0;
          if (foundCh === 0) {
            const check = await checkSourceChapter(
              { name: site.name, url: found.url, chapter: 0 },
              3000,
              manga.title
            );
            if (!check.isMismatched) {
              foundCh = check.latestChapter || 0;
            }
          }
          return { site, url: found.url, chapter: foundCh };
        }
      } catch {
        // Skip on single site error
      }
      return null;
    })
  );

  for (const res of results) {
    if (res) {
      currentSources.push({
        name: res.site.name,
        url: res.url,
        chapter: res.chapter,
      });
      discoveredCount++;

      if (res.chapter > highestCh) {
        highestCh = res.chapter;
        hasUpdate = true;
      }
    }
  }

  // Pick bestSource strictly from currentSources based on highest chapter, or fallback to first source
  let bestSource: MangaSource | null = null;
  if (currentSources.length > 0) {
    let maxVal = -1;
    for (const s of currentSources) {
      const ch = Number(s.chapter) || 0;
      if (ch > maxVal) {
        maxVal = ch;
        bestSource = s;
      }
    }
    if (!bestSource) {
      bestSource = currentSources[0];
    }
    if (maxVal > 0 && maxVal > highestCh) {
      highestCh = maxVal;
    }
  }

  const updatedManga: Manga = {
    ...manga,
    sources: currentSources,
    currentCh: highestCh > 0 ? highestCh : (Number(manga.readCh) || 0),
    lastUpdatedAt: hasUpdate || highestCh > (Number(manga.currentCh) || 0) ? todayStr : manga.lastUpdatedAt,
    latestSourceUrl: bestSource ? bestSource.url : manga.latestSourceUrl,
    latestSourceName: bestSource ? bestSource.name : manga.latestSourceName,
  };

  if (discoveredCount > 0) {
    onLog?.(`🎉 ค้นพบและเพิ่มแหล่งข้อมูลใหม่ ${discoveredCount} เว็บสำเร็จ!`);
  } else {
    onLog?.('ไม่พบบนเว็บอื่นเพิ่มเติม หรือมีลิงก์ครบทุกเว็บแล้ว');
  }

  return { updatedManga, discoveredCount };
}

